import { useEffect, useLayoutEffect } from 'react';
import { create } from 'zustand';
import { useQuery } from '@tanstack/react-query';
import type { InfoBarPrefs } from '@omnia/shared';
import { api, shouldRetry } from '../lib/api.ts';
import { defaultPrefs, deserializePrefs, parsePrefs, serializePrefs } from './prefs.ts';

const cacheKey = (userId: string) => `omnia.infobar.${userId}`;
const SAVE_DELAY_MS = 500;

interface PrefsStore {
  prefs: InfoBarPrefs;
  userId: string | null;
  /** An edit happened since sign-in, so a slower server answer must not overwrite it. */
  edited: boolean;
  load: (userId: string | null) => void;
  applyServer: (raw: unknown) => void;
  update: (patch: Partial<InfoBarPrefs>) => void;
}

function readCache(userId: string): InfoBarPrefs {
  try {
    return deserializePrefs(localStorage.getItem(cacheKey(userId)));
  } catch {
    return defaultPrefs();
  }
}

function writeCache(userId: string, prefs: InfoBarPrefs) {
  try {
    localStorage.setItem(cacheKey(userId), serializePrefs(prefs));
  } catch {
    // The server copy is the source of truth; the cache only speeds up first paint.
  }
}

let saveTimer: ReturnType<typeof setTimeout> | undefined;

function saveNow(keepalive = false) {
  clearTimeout(saveTimer);
  saveTimer = undefined;
  const { prefs, userId } = usePrefsStore.getState();
  if (!userId) return;
  api('/api/prefs/infoBar', { method: 'PUT', body: prefs, keepalive }).catch(() => {
    // Kept locally; the next change tries again.
  });
}

export const usePrefsStore = create<PrefsStore>((set, get) => ({
  prefs: defaultPrefs(),
  userId: null,
  edited: false,
  load: (userId) => {
    if (get().userId === userId) return;
    if (saveTimer) saveNow();
    set({ userId, edited: false, prefs: userId ? readCache(userId) : defaultPrefs() });
  },
  applyServer: (raw) => {
    const { userId, edited } = get();
    if (!userId || edited) return;
    const prefs = parsePrefs(raw);
    writeCache(userId, prefs);
    set({ prefs });
  },
  update: (patch) => {
    const { userId, prefs } = get();
    const next = { ...prefs, ...patch };
    set({ prefs: next, edited: true });
    if (!userId) return;
    writeCache(userId, next);
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => saveNow(), SAVE_DELAY_MS);
  },
}));

export const useInfoBarPrefs = () => usePrefsStore((state) => state.prefs);

/** Every stored preference in one request; each feature reads its own key. */
export function usePrefsQuery(userId: string | null) {
  return useQuery({
    queryKey: ['prefs', userId],
    queryFn: ({ signal }) => api<{ infoBar?: unknown; wallpaper?: unknown }>('/api/prefs', { signal }),
    enabled: userId !== null,
    retry: shouldRetry,
    staleTime: Infinity,
  });
}

/** Loads the signed-in user's prefs: the local cache before first paint, then the server copy. */
export function useInfoBarPrefsSync(userId: string | null) {
  useLayoutEffect(() => usePrefsStore.getState().load(userId), [userId]);
  const stored = usePrefsQuery(userId);
  useEffect(() => {
    if (stored.isSuccess && stored.data.infoBar !== undefined) usePrefsStore.getState().applyServer(stored.data.infoBar);
  }, [stored.isSuccess, stored.data]);
  useEffect(() => {
    // A tab closed right after a change must not lose it.
    const onHide = () => {
      if (saveTimer) saveNow(true);
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);
}
